/**
 * Core types for Night Light MVP Foundation.
 * See specs/001-mvp-foundation/data-model.md for the authoritative definitions.
 */

/** Discriminated union for an item's rights statement when present in metadata. */
export type ItemLicense =
  | { kind: "url"; value: string }
  | { kind: "statement"; value: string };

/** A fully resolved, ready-to-play archive.org video reference. */
export interface Item {
  identifier: string;
  title: string;
  year?: string;
  uploader?: string;
  license?: ItemLicense;
  durationSeconds: number;
  thumbUrl: string;
  streamUrl: string;
  encodedWidth: number;
  encodedHeight: number;
  displayAspectRatio: number;
  detailsUrl: string;
  isEmbedFallback: boolean;
}

/** An ordered channel. Persisted. */
export interface Queue {
  name: string;
  items: Item[];
  /** -1 iff items.length === 0. Otherwise a valid index. */
  currentIndex: number;
}

/** A shipped demo channel — just identifiers; items are resolved at runtime. */
export interface DemoChannel {
  name: string;
  identifiers: string[];
}

/** Transient runtime state of the current item. Never persisted. */
export interface PlaybackState {
  status: "idle" | "loading" | "playing" | "paused" | "ended" | "error";
  positionSeconds: number;
  durationSeconds: number;
  muted: boolean;
  fullscreen: boolean;
  pictureInPicture: boolean;
  aspectOverride: AspectOverride;
}

export type AspectOverride =
  | "auto"
  | "4:3"
  | "16:9"
  | "1.37"
  | "1.85"
  | "2.35"
  | "native";

/** Mirror of the archive.org Metadata API response (fields we read). */
export interface MetadataResponse {
  server: string;
  dir: string;
  files: MetadataFile[];
  metadata: {
    identifier: string;
    mediatype: "movies" | "audio" | "texts" | "image" | "collection" | string;
    title?: string;
    year?: string;
    uploader?: string;
    licenseurl?: string;
    rights?: string;
    description?: string;
    collection?: string | string[];
  };
}

export interface MetadataFile {
  name: string;
  source: "original" | "derivative" | string;
  format?: string;
  length?: string;
  width?: string;
  height?: string;
  size?: string;
}

/** localStorage schema v1 — see contracts/storage-schema.md */
export interface LibraryBlobV1 {
  version: 1;
  queue: Queue;
  settings: {
    autoplayUnlocked: boolean;
    userOwnsQueue: boolean;
  };
  updatedAt: number;
}

// ---------------------------------------------------------------------------
// CRT Shader Layer (spec 002-crt-shaders)
// ---------------------------------------------------------------------------

export type QualityTier = "low" | "medium" | "high";

export type MaskType = "shadow-mask" | "aperture-grille" | "slot-mask";

export type ChassisClass =
  | "shadow-mask-consumer"
  | "aperture-grille-consumer"
  | "broadcast-studio-monitor"
  | "early-color-consumer"
  | "rgb-computer-monitor";

export interface TVLookParams {
  compositeStrength: number;
  combFilterType: "2-line" | "3-line";
  chromaBandwidth: number;

  scanlineIntensity: number;
  scanlineSigma: number;
  adaptiveBeamAmount: number;

  maskType: MaskType;
  maskPitch: number;
  maskStrength: number;

  phosphorPersistence: number;
  phosphorTint: [number, number, number];

  bloomStrength: number;
  bloomBrightpassThreshold: number;
  halationStrength: number;
  halationSigma: number;

  convergenceOffsetR: [number, number];
  convergenceOffsetB: [number, number];

  barrelDistortion: number;
  vignetteStrength: number;

  pillarboxTint: [number, number, number] | null;
}

export interface TVLook {
  id: string;
  name: string;
  era: string;
  chassisClass: ChassisClass;
  phosphor: string;
  mask: string;
  description: string;
  motionFriendly: boolean;
  minTier: QualityTier;
  params: TVLookParams;
}

export interface Catalog {
  version: string;
  looks: TVLook[];
}

export interface DisplayPreferences {
  crtEnabled: boolean;
  selectedLookId: string | null;
  tierOverride: QualityTier | null;
}

export interface CapabilityReport {
  webgl2: boolean;
  floatFramebuffer: boolean;
  halfFloatFramebuffer: boolean;
  rVFC: boolean;
  benchmarkHighTierMs: number;
  autoSelectedTier: QualityTier;
  canDrive: boolean;
  displayRefreshHz: number;
  prefersReducedMotion: boolean;
}

export interface DisplayState {
  mounted: boolean;
  activeLook: TVLook | null;
  activeTier: QualityTier;
  tierSource: "auto" | "override";
  canDrive: boolean;
  disabledReason: string | null;
  infoPanelOpen: boolean;
}

export interface ShaderError {
  kind:
    | "webgl2-unavailable"
    | "float-fb-unavailable"
    | "compile-failed"
    | "context-lost"
    | "context-restore-failed"
    | "cannot-drive"
    | "tier-too-high"
    | "dropped-video-frame";
  message: string;
}

/** localStorage schema v2 — extends v1 with CRT display preferences */
export interface LibraryBlobV2 {
  version: 2;
  queue: Queue;
  settings: {
    autoplayUnlocked: boolean;
    userOwnsQueue: boolean;
  };
  display: DisplayPreferences;
  updatedAt: number;
}

export const defaultDisplayPreferences: DisplayPreferences = {
  crtEnabled: false,
  selectedLookId: null,
  tierOverride: null,
};
